/**
 * TARS column chart — vertical columns (standard “column” chart).
 * Categories on the X axis (bottom); values on the Y axis (read by height).
 * Same table JSON as `TARSBarChart` via `tarsBarChartHelpers.ts`.
 *
 * `TARSBarChart` is the horizontal bar variant (categories on Y, values on X).
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-ignore amCharts5 bundled IIFE registers globals
import "../../../libs/amcharts5/index";
// @ts-ignore amCharts5 xy bundle
import "../../../libs/amcharts5/xy";
// @ts-ignore Animated theme registers am5themes_Animated
import "../../../libs/amcharts5/themes/Animated";

import type { MutableRefObject } from "react";
import { useLayoutEffect, useMemo, useRef } from "react";
import { TARS_COLUMN_BAR_CORNER_RADIUS_PX } from "./tars-chart-column-style";
import {
    orderBarChartPoints,
    resolveTarsBarColumnValueSuffix,
    rowsToTARSBarSeries,
    resolveLabelFillHex,
    type TARSBarChartPoint,
    type TARSBarChartTableData,
    type TARSChartConfiguration,
} from "./tarsBarChartHelpers";
import { bindBarColumnFillAdapter } from "./tarsChartFillHelpers";
import { resolveTarsChartFontSizes, type TarsChartFontSizes } from "./tarsChartFontHelpers";
import { attachAm5ExportingToRoot, type Am5ChartExportApi } from "./tarsAm5Exporting";
import { prependRootTitleIfAny } from "./tarsAm5RootTitle";
import { bindAm5ExportRefs, clearAm5ExportRefs } from "./tars-chart-export-refs";
import TARSChartCopyImageToolbar from "./TARSChartCopyImageToolbar";

const TARS_COLUMN_VALUE_NUMBER_FORMAT = "#.#";

function hideAxisTitleIfPresent(axis: any): void {
    try {
        const title = axis.get("title");
        if (title?.set) title.set("visible", false);
    } catch {
        /* optional */
    }
}

function buildColumnChartOnRoot(
    amChartsCore: any,
    amChartsXY: any,
    chartRoot: any,
    sortedPoints: TARSBarChartPoint[],
    categoryLabelFill: string,
    valueLabelFill: string,
    valueSuffix: string,
    fonts: TarsChartFontSizes
): void {
    const valueNumberText = `{valueY.formatNumber('${TARS_COLUMN_VALUE_NUMBER_FORMAT}')}`;
    const formattedValueText =
        valueSuffix === "" ? `[bold]${valueNumberText}[/]` : `[bold]${valueNumberText}[/]${valueSuffix}`;
    const valueBulletText = formattedValueText;
    const valueTooltipText = `{categoryX}: ${formattedValueText}`;
    const xyChart = chartRoot.container.children.push(
        amChartsXY.XYChart.new(chartRoot, {
            panX: false,
            panY: false,
            wheelX: "none",
            wheelY: "none",
            paddingTop: 36,
            paddingLeft: 8,
            paddingRight: 8,
            paddingBottom: 20,
        })
    );

    xyChart.plotContainer.set("paddingBottom", 30);

    const categoryCount = Math.max(1, sortedPoints.length);

    const xCategoryAxis = xyChart.xAxes.push(
        amChartsXY.CategoryAxis.new(chartRoot, {
            categoryField: "label",
            renderer: amChartsXY.AxisRendererX.new(chartRoot, {
                minGridDistance: 10,
                paddingTop: 1,
            }),
        })
    );

    hideAxisTitleIfPresent(xCategoryAxis);
    const xLabelTemplate = xCategoryAxis.get("renderer").labels.template;
    xLabelTemplate.setAll({
        visible: true,
        fontSize: fonts.axis,
        fontWeight: "700",
        fill: amChartsCore.color(categoryLabelFill),
        rotation: 0,
        textAlign: "center",
        oversizedBehavior: "wrap",
        paddingTop: 12,
    });
    xLabelTemplate.adapters.add("maxWidth", () => {
        const cw = chartRoot.dom?.clientWidth ?? 0;
        const basis = cw > 0 ? cw : 400;
        const slot = Math.floor(((basis - 40) / categoryCount) * 0.9);
        return Math.max(72, Math.min(220, slot));
    });
    xCategoryAxis.get("renderer").grid.template.setAll({ visible: false });

    const yValueAxis = xyChart.yAxes.push(
        amChartsXY.ValueAxis.new(chartRoot, {
            extraMax: 0.06,
            renderer: amChartsXY.AxisRendererY.new(chartRoot, {}),
        })
    );

    hideAxisTitleIfPresent(yValueAxis);
    yValueAxis.get("renderer").labels.template.setAll({ visible: false });
    yValueAxis.get("renderer").grid.template.setAll({ visible: false });

    const columnSeries = xyChart.series.push(
        amChartsXY.ColumnSeries.new(chartRoot, {
            name: "Series",
            xAxis: xCategoryAxis,
            yAxis: yValueAxis,
            valueYField: "value",
            categoryXField: "label",
            maskBullets: false,
        })
    );

    columnSeries.columns.template.setAll({
        width: amChartsCore.percent(55),
        fill: amChartsCore.color("#fff"),
        strokeWidth: 0,
        strokeOpacity: 0,
        cornerRadiusTL: TARS_COLUMN_BAR_CORNER_RADIUS_PX,
        cornerRadiusTR: TARS_COLUMN_BAR_CORNER_RADIUS_PX,
        cornerRadiusBL: 0,
        cornerRadiusBR: 0,
        tooltipText: valueTooltipText,
    });

    bindBarColumnFillAdapter(amChartsCore, columnSeries);

    columnSeries.bullets.push(() =>
        amChartsCore.Bullet.new(chartRoot, {
            locationX: 0.5,
            locationY: 1,
            sprite: amChartsCore.Label.new(chartRoot, {
                text: valueBulletText,
                fill: amChartsCore.color(valueLabelFill),
                centerX: amChartsCore.p50,
                centerY: amChartsCore.p100,
                dy: 0,
                fontSize: fonts.dataLabel,
                fontWeight: "700",
                populateText: true,
            }),
        })
    );

    columnSeries.data.setAll(sortedPoints);
    xCategoryAxis.data.setAll(sortedPoints);
    xyChart.set("cursor", amChartsXY.XYCursor.new(chartRoot, { behavior: "none" }));
    columnSeries.appear(1000);
    xyChart.appear(1000, 100);
}

function mountAm5ColumnChartInContainer(
    chartContainerElement: HTMLElement,
    barSeriesPoints: TARSBarChartPoint[],
    chartTitle: string | null,
    configuration: TARSChartConfiguration | undefined,
    valueSuffix: string
): { dispose: () => void; exportApi: Am5ChartExportApi } | null {
    if (typeof window === "undefined" || barSeriesPoints.length === 0) return null;

    const amChartsCore = (window as any).am5;
    const amChartsXY = (window as any).am5xy;
    const animatedTheme = (window as any).am5themes_Animated;

    if (!amChartsCore?.Root?.new || !amChartsXY) {
        console.warn("TARSColumnChart: amCharts globals not ready");
        return null;
    }

    const chartRoot = amChartsCore.Root.new(chartContainerElement);
    if (animatedTheme) {
        chartRoot.setThemes([animatedTheme.new(chartRoot)]);
    }
    const fonts = resolveTarsChartFontSizes(configuration);
    prependRootTitleIfAny(amChartsCore, chartRoot, chartTitle, fonts.title);

    const categoryLabelFill = resolveLabelFillHex(configuration?.labelColors, "category");
    const valueLabelFill = resolveLabelFillHex(configuration?.labelColors, "value");

    try {
        buildColumnChartOnRoot(
            amChartsCore,
            amChartsXY,
            chartRoot,
            barSeriesPoints,
            categoryLabelFill,
            valueLabelFill,
            valueSuffix,
            fonts
        );
    } catch (err) {
        console.error("TARSColumnChart: failed to build chart", err);
        chartRoot.dispose();
        return null;
    }

    requestAnimationFrame(() => {
        try {
            chartRoot.resize();
        } catch {
            /* optional */
        }
    });

    const exportApi = attachAm5ExportingToRoot(chartRoot);
    return { dispose: () => chartRoot.dispose(), exportApi };
}

export interface TARSColumnChartProps {
    data: TARSBarChartTableData;
    id?: string;
    /** When set, copy control is expected in the parent; this ref receives the copy handler. */
    chartCopyRef?: MutableRefObject<(() => Promise<void>) | null>;
    /** Live PNG blob export for S3 publish (Chart view must be mounted). */
    chartExportRef?: MutableRefObject<(() => Promise<Blob>) | null>;
}

export default function TARSColumnChart({
    data,
    id = "tars-column-chart",
    chartCopyRef: chartCopyRefProp,
    chartExportRef,
}: TARSColumnChartProps) {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const disposeChartRef = useRef<(() => void) | null>(null);
    const internalChartCopyRef = useRef<(() => Promise<void>) | null>(null);
    const copyRef = chartCopyRefProp ?? internalChartCopyRef;
    const seriesPoints = useMemo(
        () => orderBarChartPoints(rowsToTARSBarSeries(data), data.preserveRowOrder),
        [data]
    );
    const valueSuffix = useMemo(
        () =>
            resolveTarsBarColumnValueSuffix(
                Array.isArray(data.headers) ? data.headers : [],
                data.configuration,
                seriesPoints.map((p) => p.value)
            ),
        [data.headers, data.configuration, seriesPoints]
    );
    const chartTitle = typeof data.title === "string" && data.title.trim() !== "" ? data.title.trim() : null;

    useLayoutEffect(() => {
        const chartContainerElement = chartContainerRef.current;

        disposeChartRef.current?.();
        disposeChartRef.current = null;
        clearAm5ExportRefs(copyRef, chartExportRef);

        if (!chartContainerElement || seriesPoints.length === 0) return undefined;

        const handle = mountAm5ColumnChartInContainer(
            chartContainerElement,
            seriesPoints,
            chartTitle,
            data.configuration,
            valueSuffix
        );

        disposeChartRef.current = handle ? () => handle.dispose() : null;
        bindAm5ExportRefs(handle?.exportApi, copyRef, chartExportRef);

        return () => {
            disposeChartRef.current?.();
            disposeChartRef.current = null;
            clearAm5ExportRefs(copyRef, chartExportRef);
        };
    }, [seriesPoints, chartTitle, data.configuration, valueSuffix]);

    if (seriesPoints.length === 0) {
        return (
            <div className="flex flex-1 items-center justify-center px-8 py-16 text-[13px] font-medium text-slate-400">
                No numeric rows to display. Paste JSON with headers and rows.
            </div>
        );
    }

    return (
        <div className="flex min-h-0 w-full flex-1 flex-col">
            {!chartCopyRefProp ? (
                <div className="mb-2 flex shrink-0 justify-end">
                    <TARSChartCopyImageToolbar copyRef={internalChartCopyRef} />
                </div>
            ) : null}
            <div
                ref={chartContainerRef}
                id={id}
                className="min-h-0 min-h-[280px] w-full flex-1 basis-0"
                style={{ minHeight: "min(480px, 50vh)" }}
            />
        </div>
    );
}
